import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import { Sparkles, ArrowRight, CheckCircle2, Send } from "lucide-react";

export const ProgramBuilder = ({ programKey }) => {
  const [cfg, setCfg] = useState(null);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ company: "", contact_name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {lead_id, mode, quote, recipient_url}
  const [exactCount, setExactCount] = useState("");

  const qtyQid = cfg?.questions.find((q) => q.options.some((o) => o.qty))?.id;
  const isDirect = Object.values(answers).map(String).includes("direct");

  useEffect(() => {
    api.get(`/programs/config/${programKey}`).then((r) => {
      setCfg(r.data);
      const init = {};
      r.data.questions.forEach((q) => { init[q.id] = q.options[0]?.value ?? ""; });
      setAnswers(init);
    }).catch(() => {});
  }, [programKey]);

  const setC = (k) => (e) => setContact({ ...contact, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/programs/quote", {
        key: programKey, answers, exact_quantity: Number(exactCount) || 0, ...contact, origin: window.location.origin,
      });
      setResult(data);
      if (data.mode === "quote") toast.success("Quote request sent — we'll review it personally 💛");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setBusy(false); }
  };

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/programs/checkout", { lead_id: result.lead_id, origin: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setBusy(false);
    }
  };

  if (!cfg) return null;

  return (
    <section className="bg-[#3A3632] py-20" data-testid={`program-builder-${programKey}`}>
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center">
          <span className="gh-overline text-[#D9CBA0] inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Build your package</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#FAF7F2] mt-4">Get an instant estimate</h2>
          <p className="text-[#D9CBA0]/90 mt-3">{cfg.intro}</p>
        </div>

        {!result && (
          <form onSubmit={submit} className="mt-10 bg-[#FAF7F2] rounded-2xl p-8" data-testid="builder-form">
            <div className="grid sm:grid-cols-2 gap-5">
              {cfg.questions.map((q) => (
                <label key={q.id} className="block">
                  <span className="text-sm text-[#3A3632] font-medium mb-1.5 block">{q.label}</span>
                  <select value={answers[q.id] ?? ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    data-testid={`builder-q-${q.id}`} className="gh-input">
                    {q.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {q.help && <span className="text-xs text-[#8A8175] mt-1 block">{q.help}</span>}
                  {q.id === qtyQid && (
                    <input type="number" min="1" value={exactCount} onChange={(e) => setExactCount(e.target.value)}
                      placeholder="Or enter the exact number (optional)" data-testid="builder-exact-count"
                      className="gh-input mt-2 text-sm" />
                  )}
                </label>
              ))}
            </div>
            {isDirect && (
              <p className="text-xs text-[#6B655F] bg-[#8E9A86]/10 border border-[#8E9A86]/25 rounded-lg px-4 py-3 mt-4" data-testid="builder-privacy-note">
                🔒 You chose direct-to-recipient delivery. Recipient details are collected <strong>securely after your order</strong> via a private link and are never stored in this form.
              </p>
            )}
            <div className="gh-hairline my-6" />
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Organization</span>
                <input required value={contact.company} onChange={setC("company")} data-testid="builder-company" className="gh-input" /></label>
              <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Your name</span>
                <input required value={contact.contact_name} onChange={setC("contact_name")} data-testid="builder-name" className="gh-input" /></label>
              <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Email</span>
                <input type="email" required value={contact.email} onChange={setC("email")} data-testid="builder-email" className="gh-input" /></label>
              <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Phone <span className="text-[#8A8175] font-normal">(optional)</span></span>
                <input value={contact.phone} onChange={setC("phone")} data-testid="builder-phone" className="gh-input" /></label>
            </div>
            <label className="block mt-4"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Anything else? <span className="text-[#8A8175] font-normal">(optional)</span></span>
              <textarea rows={3} value={contact.notes} onChange={setC("notes")} data-testid="builder-notes" className="gh-input resize-none" /></label>
            <button type="submit" disabled={busy} data-testid="builder-submit" className="w-full mt-5 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? "Calculating…" : <>See my estimate <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
        )}

        {result && (
          <div className="mt-10 bg-[#FAF7F2] rounded-2xl p-8" data-testid="builder-result">
            <div className="text-center">
              <div className="h-12 w-12 rounded-full bg-[#8E9A86]/12 mx-auto flex items-center justify-center mb-3"><CheckCircle2 className="h-6 w-6 text-[#8E9A86]" /></div>
              <div className="gh-overline">Your estimate</div>
              <div className="font-heading text-5xl text-[#3A3632] mt-2" data-testid="builder-total">{money(result.quote.total)}</div>
              <p className="text-sm text-[#8A8175] mt-1">{result.quote.quantity} boxes · estimate only, charged in USD</p>
              <p className="text-xs text-[#8A8175] mt-2 max-w-md mx-auto">💛 Bulk program orders are lovingly handmade to order and typically ship within <strong>2–4 weeks</strong>. We'll keep you updated every step.</p>
            </div>
            <div className="mt-6 border-t border-b border-[#D9CBA0] py-4 space-y-1.5">
              {result.quote.lines.map((l, i) => (
                <div key={i} className="flex justify-between text-sm text-[#6B655F]"><span>{l.question}</span><span className="text-[#3A3632] font-medium text-right ml-4">{l.choice}</span></div>
              ))}
            </div>

            {result.recipient_url && (
              <div className="mt-5 bg-[#8E9A86]/10 border border-[#8E9A86]/25 rounded-lg px-4 py-3 text-sm text-[#3A3632]" data-testid="builder-recipient-link">
                🔒 <strong>Direct delivery selected.</strong> Add your recipient addresses securely here:{" "}
                <a href={result.recipient_url} className="text-[#8E9A86] underline break-all">{result.recipient_url}</a>
                <span className="block text-xs text-[#8A8175] mt-1">We'll also email you this private link. Recipient details are never stored in a public form.</span>
              </div>
            )}

            {result.mode === "self_serve" ? (
              <>
                <button onClick={checkout} disabled={busy} data-testid="builder-checkout" className="w-full mt-6 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? "Starting checkout…" : <>Proceed to secure checkout <ArrowRight className="h-4 w-4" /></>}
                </button>
                <p className="text-xs text-[#8A8175] text-center mt-3">Prefer to talk first? <button onClick={() => setResult(null)} className="underline">Adjust your package</button></p>
              </>
            ) : (
              <div className="mt-6 text-center">
                <div className="inline-flex items-center gap-2 bg-[#8E9A86]/12 text-[#3A3632] rounded-full px-4 py-2 text-sm"><Send className="h-4 w-4 text-[#8E9A86]" /> Quote request submitted</div>
                <p className="text-[#6B655F] mt-4 leading-relaxed">Because this is a larger order, we'll personally review it and email <strong>{contact.email}</strong> a secure payment link shortly. Bulk orders are handmade and typically ship within <strong>2–4 weeks</strong>. Thank you for trusting Hope &amp; Favor 💛</p>
                <button onClick={() => { setResult(null); }} data-testid="builder-restart" className="text-sm text-[#8E9A86] underline mt-4">Build another package</button>
              </div>
            )}
          </div>
        )}
        <style>{`.gh-input{width:100%;border:1px solid #D1CCC5;border-radius:0.6rem;background:#fff;padding:0.7rem 0.9rem;font-size:0.95rem;outline:none;transition:all .2s}.gh-input:focus{border-color:#8E9A86;box-shadow:0 0 0 2px rgba(142,154,134,.25)}`}</style>
      </div>
    </section>
  );
};
